/**
 * Estimates Page
 * BuilderBel Construction Project Management System
 *
 * Pre-Construction estimate management with line items, markups, and workflow
 */

import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FileSpreadsheet, Plus, ArrowLeft, Layers, Edit2, X, AlertTriangle } from 'lucide-react';
import { ModuleLayout, EmptyState } from '../components/ui';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import { useToast } from '../contexts/ToastContext';
import {
  EstimateHeader,
  EstimateList,
  EstimateFilters,
  EstimateDrawer,
  LineItemTable,
  LineItemDrawer,
  MarkupsSection,
  EstimateSummaryPanel,
  StatusBadge,
} from '../components/estimates';
import {
  useGetEstimatesQuery,
  useGetEstimateQuery,
  useCreateEstimateMutation,
  useUpdateEstimateMutation,
  useDeleteEstimateMutation,
  useGetEstimateLineItemsQuery,
  useCreateEstimateLineItemMutation,
  useUpdateEstimateLineItemMutation,
  useDeleteEstimateLineItemMutation,
  useGetMarkupsQuery,
  useCreateMarkupMutation,
  useUpdateMarkupMutation,
  useDeleteMarkupMutation,
  useRecalculateMarkupsMutation,
  useSubmitEstimateForReviewMutation,
  useApproveEstimateMutation,
  useRejectEstimateMutation,
  useCreateEstimateVersionMutation,
  useGetEstimateSummaryQuery,
} from '../services/estimateApi';
import type {
  Estimate,
  EstimateFilterParams,
  CreateEstimateRequest,
  UpdateEstimateRequest,
  CreateLineItemRequest,
  UpdateLineItemRequest,
  CreateMarkupRequest,
  UpdateMarkupRequest,
  EstimateLineItem,
} from '../types/estimate';

type ViewMode = 'list' | 'detail';
type GroupBy = 'none' | 'category';

interface ConfirmModalState {
  isOpen: boolean;
  title: string;
  message: string;
  variant: 'danger' | 'warning' | 'info';
  showTextarea?: boolean;
  textareaLabel?: string;
  textareaRequired?: boolean;
  confirmLabel: string;
  onConfirm: (notes?: string) => void;
}

const initialConfirmState: ConfirmModalState = {
  isOpen: false,
  title: '',
  message: '',
  variant: 'info',
  confirmLabel: 'Confirm',
  onConfirm: () => {},
};

const ConfirmationModal: React.FC<{
  state: ConfirmModalState;
  onClose: () => void;
}> = ({ state, onClose }) => {
  const [notes, setNotes] = useState('');

  if (!state.isOpen) return null;

  const variantStyles = {
    danger: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    info: 'bg-brand-600 hover:bg-brand-700',
  };

  const handleConfirm = () => {
    if (state.textareaRequired && !notes.trim()) return;
    state.onConfirm(notes || undefined);
    setNotes('');
    onClose();
  };

  const handleClose = () => {
    setNotes('');
    onClose();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={handleClose} />
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${state.variant === 'danger' ? 'bg-red-50' : state.variant === 'warning' ? 'bg-yellow-50' : 'bg-brand-50'}`}
              >
                <AlertTriangle
                  className={`h-5 w-5 ${state.variant === 'danger' ? 'text-red-600' : state.variant === 'warning' ? 'text-yellow-600' : 'text-brand-600'}`}
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{state.title}</h3>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-6 py-4">
            <p className="text-sm text-gray-600">{state.message}</p>
            {state.showTextarea && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {state.textareaLabel || 'Notes'}
                  {state.textareaRequired && ' *'}
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder={state.textareaRequired ? 'Required...' : 'Optional notes...'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={state.textareaRequired && !notes.trim()}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${variantStyles[state.variant]}`}
            >
              {state.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const Estimates: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('category');

  // Filters
  const [filters, setFilters] = useState<EstimateFilterParams>({});

  // Drawer states
  const [estimateDrawerOpen, setEstimateDrawerOpen] = useState(false);
  const [lineItemDrawerOpen, setLineItemDrawerOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState<Estimate | null>(null);
  const [editingLineItem, setEditingLineItem] = useState<EstimateLineItem | null>(null);

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(initialConfirmState);

  // API Queries
  const { data: estimatesData, isLoading: estimatesLoading } = useGetEstimatesQuery(
    { projectId: projectId!, filters },
    { skip: !projectId }
  );

  const { data: estimateData, isLoading: estimateLoading } = useGetEstimateQuery(
    { projectId: projectId!, estimateId: selectedEstimateId! },
    { skip: !projectId || !selectedEstimateId }
  );

  const { data: lineItemsData, isLoading: lineItemsLoading } = useGetEstimateLineItemsQuery(
    { projectId: projectId!, estimateId: selectedEstimateId! },
    { skip: !projectId || !selectedEstimateId }
  );

  const { data: markupsData, isLoading: markupsLoading } = useGetMarkupsQuery(
    { projectId: projectId!, estimateId: selectedEstimateId! },
    { skip: !projectId || !selectedEstimateId }
  );

  const { data: summaryData } = useGetEstimateSummaryQuery(
    { projectId: projectId!, estimateId: selectedEstimateId! },
    { skip: !projectId || !selectedEstimateId }
  );

  // Mutations
  const [createEstimate, { isLoading: creatingEstimate }] = useCreateEstimateMutation();
  const [updateEstimate, { isLoading: updatingEstimate }] = useUpdateEstimateMutation();
  const [deleteEstimate] = useDeleteEstimateMutation();
  const [createLineItem, { isLoading: creatingLineItem }] = useCreateEstimateLineItemMutation();
  const [updateLineItem, { isLoading: updatingLineItem }] = useUpdateEstimateLineItemMutation();
  const [deleteLineItem] = useDeleteEstimateLineItemMutation();
  const [createMarkup] = useCreateMarkupMutation();
  const [updateMarkup] = useUpdateMarkupMutation();
  const [deleteMarkup] = useDeleteMarkupMutation();
  const [recalculateMarkups, { isLoading: recalculating }] = useRecalculateMarkupsMutation();
  const [submitForReview] = useSubmitEstimateForReviewMutation();
  const [approveEstimate] = useApproveEstimateMutation();
  const [rejectEstimate] = useRejectEstimateMutation();
  const [createVersion] = useCreateEstimateVersionMutation();

  // Derived data
  const estimates = estimatesData?.data?.estimates || [];
  const selectedEstimate = estimateData?.data;
  const lineItems = lineItemsData?.data?.lineItems || [];
  const markups = markupsData?.data || [];
  const summary = summaryData?.data || null;

  const isEditable = selectedEstimate?.status === 'draft';

  // Handlers
  const handleSelectEstimate = useCallback((estimate: Estimate) => {
    setSelectedEstimateId(estimate._id);
    setViewMode('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewMode('list');
    setSelectedEstimateId(null);
  }, []);

  const handleCreateEstimate = useCallback(() => {
    setEditingEstimate(null);
    setEstimateDrawerOpen(true);
  }, []);

  const handleEditEstimate = useCallback(
    (estimate?: Estimate) => {
      const target = estimate || selectedEstimate;
      if (target) {
        setEditingEstimate(target);
        setEstimateDrawerOpen(true);
      }
    },
    [selectedEstimate]
  );

  const handleEstimateSubmit = useCallback(
    async (data: CreateEstimateRequest | UpdateEstimateRequest) => {
      if (!projectId) return;

      try {
        if (editingEstimate) {
          await updateEstimate({
            projectId,
            estimateId: editingEstimate._id,
            data: data as UpdateEstimateRequest,
          }).unwrap();
          toast.success('Estimate updated', 'Estimate has been updated successfully.');
        } else {
          const result = await createEstimate({
            projectId,
            data: data as CreateEstimateRequest,
          }).unwrap();
          toast.success('Estimate created', 'New estimate has been created successfully.');
          if (result.data?._id) {
            setSelectedEstimateId(result.data._id);
            setViewMode('detail');
          }
        }
        setEstimateDrawerOpen(false);
        setEditingEstimate(null);
      } catch (error: any) {
        toast.error(
          'Failed to save estimate',
          error?.data?.message || 'An unexpected error occurred.'
        );
      }
    },
    [projectId, editingEstimate, createEstimate, updateEstimate, toast]
  );

  const handleDeleteEstimate = useCallback(
    (estimate: Estimate) => {
      if (!projectId) return;

      setConfirmModal({
        isOpen: true,
        title: 'Delete Estimate',
        message: `Are you sure you want to delete "${estimate.name}"? This action cannot be undone.`,
        variant: 'danger',
        confirmLabel: 'Delete',
        onConfirm: async () => {
          try {
            await deleteEstimate({
              projectId,
              estimateId: estimate._id,
            }).unwrap();
            toast.success('Estimate deleted', 'Estimate has been deleted successfully.');
            if (selectedEstimateId === estimate._id) {
              handleBackToList();
            }
          } catch (error: any) {
            toast.error(
              'Failed to delete estimate',
              error?.data?.message || 'An unexpected error occurred.'
            );
          }
        },
      });
    },
    [projectId, selectedEstimateId, deleteEstimate, handleBackToList, toast]
  );

  // Line Item Handlers
  const handleAddLineItem = useCallback(() => {
    setEditingLineItem(null);
    setLineItemDrawerOpen(true);
  }, []);

  const handleEditLineItem = useCallback((lineItem: EstimateLineItem) => {
    setEditingLineItem(lineItem);
    setLineItemDrawerOpen(true);
  }, []);

  const handleLineItemSubmit = useCallback(
    async (data: CreateLineItemRequest | UpdateLineItemRequest) => {
      if (!projectId || !selectedEstimateId) return;

      try {
        if (editingLineItem) {
          await updateLineItem({
            projectId,
            estimateId: selectedEstimateId,
            lineItemId: editingLineItem._id,
            data: data as UpdateLineItemRequest,
          }).unwrap();
          toast.success('Line item updated');
        } else {
          await createLineItem({
            projectId,
            estimateId: selectedEstimateId,
            data: data as CreateLineItemRequest,
          }).unwrap();
          toast.success('Line item added');
        }
        setLineItemDrawerOpen(false);
        setEditingLineItem(null);
      } catch (error: any) {
        toast.error(
          'Failed to save line item',
          error?.data?.message || 'An unexpected error occurred.'
        );
      }
    },
    [projectId, selectedEstimateId, editingLineItem, createLineItem, updateLineItem, toast]
  );

  const handleDeleteLineItem = useCallback(
    (lineItem: EstimateLineItem) => {
      if (!projectId || !selectedEstimateId) return;

      setConfirmModal({
        isOpen: true,
        title: 'Delete Line Item',
        message: `Are you sure you want to delete "${lineItem.description}"?`,
        variant: 'danger',
        confirmLabel: 'Delete',
        onConfirm: async () => {
          try {
            await deleteLineItem({
              projectId,
              estimateId: selectedEstimateId,
              lineItemId: lineItem._id,
            }).unwrap();
            toast.success('Line item deleted');
          } catch (error: any) {
            toast.error(
              'Failed to delete line item',
              error?.data?.message || 'An unexpected error occurred.'
            );
          }
        },
      });
    },
    [projectId, selectedEstimateId, deleteLineItem, toast]
  );

  // Markup Handlers
  const handleAddMarkup = useCallback(
    async (data: CreateMarkupRequest) => {
      if (!projectId || !selectedEstimateId) return;

      try {
        await createMarkup({
          projectId,
          estimateId: selectedEstimateId,
          data,
        }).unwrap();
        toast.success('Markup added');
      } catch (error: any) {
        toast.error(
          'Failed to add markup',
          error?.data?.message || 'An unexpected error occurred.'
        );
      }
    },
    [projectId, selectedEstimateId, createMarkup, toast]
  );

  const handleUpdateMarkup = useCallback(
    async (markupId: string, data: UpdateMarkupRequest) => {
      if (!projectId || !selectedEstimateId) return;

      try {
        await updateMarkup({
          projectId,
          estimateId: selectedEstimateId,
          markupId,
          data,
        }).unwrap();
        toast.success('Markup updated');
      } catch (error: any) {
        toast.error(
          'Failed to update markup',
          error?.data?.message || 'An unexpected error occurred.'
        );
      }
    },
    [projectId, selectedEstimateId, updateMarkup, toast]
  );

  const handleDeleteMarkup = useCallback(
    (markupId: string) => {
      if (!projectId || !selectedEstimateId) return;

      setConfirmModal({
        isOpen: true,
        title: 'Delete Markup',
        message: 'Are you sure you want to delete this markup?',
        variant: 'danger',
        confirmLabel: 'Delete',
        onConfirm: async () => {
          try {
            await deleteMarkup({
              projectId,
              estimateId: selectedEstimateId,
              markupId,
            }).unwrap();
            toast.success('Markup deleted');
          } catch (error: any) {
            toast.error(
              'Failed to delete markup',
              error?.data?.message || 'An unexpected error occurred.'
            );
          }
        },
      });
    },
    [projectId, selectedEstimateId, deleteMarkup, toast]
  );

  const handleRecalculateMarkups = useCallback(async () => {
    if (!projectId || !selectedEstimateId) return;

    try {
      await recalculateMarkups({
        projectId,
        estimateId: selectedEstimateId,
      }).unwrap();
      toast.success('Markups recalculated');
    } catch (error: any) {
      toast.error(
        'Failed to recalculate markups',
        error?.data?.message || 'An unexpected error occurred.'
      );
    }
  }, [projectId, selectedEstimateId, recalculateMarkups, toast]);

  // Workflow Handlers
  const handleSubmitForReview = useCallback(
    (estimate: Estimate) => {
      if (!projectId) return;

      setConfirmModal({
        isOpen: true,
        title: 'Submit for Review',
        message: `Submit "${estimate.name}" for review? Once submitted, the estimate cannot be edited until it is approved or rejected.`,
        variant: 'info',
        confirmLabel: 'Submit for Review',
        onConfirm: async () => {
          try {
            await submitForReview({
              projectId,
              estimateId: estimate._id,
            }).unwrap();
            toast.success('Submitted for review', 'Estimate has been submitted for review.');
          } catch (error: any) {
            toast.error(
              'Failed to submit for review',
              error?.data?.message || 'An unexpected error occurred.'
            );
          }
        },
      });
    },
    [projectId, submitForReview, toast]
  );

  const handleApprove = useCallback(
    (estimate: Estimate) => {
      if (!projectId) return;

      setConfirmModal({
        isOpen: true,
        title: 'Approve Estimate',
        message: `Approve "${estimate.name}"? This will mark the estimate as approved.`,
        variant: 'info',
        showTextarea: true,
        textareaLabel: 'Approval Notes',
        confirmLabel: 'Approve',
        onConfirm: async (notes?: string) => {
          try {
            await approveEstimate({
              projectId,
              estimateId: estimate._id,
              data: { notes },
            }).unwrap();
            toast.success('Estimate approved', 'Estimate has been approved successfully.');
          } catch (error: any) {
            toast.error(
              'Failed to approve estimate',
              error?.data?.message || 'An unexpected error occurred.'
            );
          }
        },
      });
    },
    [projectId, approveEstimate, toast]
  );

  const handleReject = useCallback(
    (estimate: Estimate) => {
      if (!projectId) return;

      setConfirmModal({
        isOpen: true,
        title: 'Reject Estimate',
        message: `Reject "${estimate.name}"? Please provide a reason for rejection.`,
        variant: 'warning',
        showTextarea: true,
        textareaLabel: 'Rejection Reason',
        textareaRequired: true,
        confirmLabel: 'Reject',
        onConfirm: async (reason?: string) => {
          if (!reason) return;
          try {
            await rejectEstimate({
              projectId,
              estimateId: estimate._id,
              data: { reason },
            }).unwrap();
            toast.success('Estimate rejected', 'Estimate has been rejected.');
          } catch (error: any) {
            toast.error(
              'Failed to reject estimate',
              error?.data?.message || 'An unexpected error occurred.'
            );
          }
        },
      });
    },
    [projectId, rejectEstimate, toast]
  );

  const handleCreateVersion = useCallback(
    async (estimate: Estimate) => {
      if (!projectId) return;

      try {
        const result = await createVersion({
          projectId,
          estimateId: estimate._id,
        }).unwrap();
        toast.success(
          'New version created',
          `Version ${(estimate.version || 1) + 1} has been created.`
        );
        if (result.data?._id) {
          setSelectedEstimateId(result.data._id);
        }
      } catch (error: any) {
        toast.error(
          'Failed to create version',
          error?.data?.message || 'An unexpected error occurred.'
        );
      }
    },
    [projectId, createVersion, toast]
  );

  // No project selected
  if (!projectId) {
    return (
      <ModuleLayout
        title="Estimates"
        description="Manage project cost estimates"
        icon={<FileSpreadsheet className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={FileSpreadsheet}
          title="No project selected"
          description="Please select a project from the header to view and manage estimates."
        />
      </ModuleLayout>
    );
  }

  // Detail View
  if (viewMode === 'detail' && selectedEstimate) {
    return (
      <ModuleLayout
        title={selectedEstimate.name}
        description={selectedEstimate.description || 'Cost estimate details'}
        icon={<FileSpreadsheet className="h-4 w-4 text-brand-600" />}
        primaryAction={
          isEditable
            ? {
                label: 'Add Line Item',
                onClick: handleAddLineItem,
                icon: <Plus className="h-4 w-4 mr-2" />,
              }
            : undefined
        }
        stats={[]}
      >
        {/* Back Button & Status */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Estimates
          </button>
          <div className="flex items-center gap-3">
            {isEditable && (
              <button
                onClick={() => handleEditEstimate()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Edit2 className="h-3.5 w-3.5" />
                Edit
              </button>
            )}
            <StatusBadge status={selectedEstimate.status} size="lg" />
            {selectedEstimate.version > 1 && (
              <span className="text-sm text-gray-500">Version {selectedEstimate.version}</span>
            )}
          </div>
        </div>

        {/* Summary Header */}
        <EstimateHeader
          subtotalDirect={selectedEstimate.subtotalDirect}
          totalMarkup={selectedEstimate.totalMarkup}
          grandTotal={selectedEstimate.grandTotal}
          subtotalLabor={selectedEstimate.subtotalLabor}
          subtotalMaterial={selectedEstimate.subtotalMaterial}
          subtotalEquipment={selectedEstimate.subtotalEquipment}
          isLoading={estimateLoading}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Line Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Grouping Controls & Add Button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
                <Layers className="h-4 w-4 text-gray-400" />
                <select
                  value={groupBy}
                  onChange={e => setGroupBy(e.target.value as GroupBy)}
                  className="text-sm border-0 focus:ring-0 pr-8 bg-transparent"
                >
                  <option value="none">No Grouping</option>
                  <option value="category">Group by Category</option>
                </select>
              </div>
              {isEditable && (
                <button
                  onClick={handleAddLineItem}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                >
                  <Plus className="h-4 w-4" />
                  Add Line Item
                </button>
              )}
            </div>

            {/* Line Items Table */}
            <LineItemTable
              lineItems={lineItems}
              onEdit={handleEditLineItem}
              onDelete={handleDeleteLineItem}
              groupBy={groupBy}
              isEditable={isEditable}
              isLoading={lineItemsLoading}
            />

            {/* Markups Section */}
            <MarkupsSection
              markups={markups}
              subtotalDirect={selectedEstimate.subtotalDirect}
              subtotalLabor={selectedEstimate.subtotalLabor}
              subtotalMaterial={selectedEstimate.subtotalMaterial}
              subtotalEquipment={selectedEstimate.subtotalEquipment}
              subtotalSubcontractor={selectedEstimate.subtotalSubcontractor}
              onAdd={handleAddMarkup}
              onUpdate={handleUpdateMarkup}
              onDelete={handleDeleteMarkup}
              onRecalculate={handleRecalculateMarkups}
              isEditable={isEditable}
              isLoading={markupsLoading || recalculating}
            />
          </div>

          {/* Sidebar - Summary */}
          <div className="lg:col-span-1">
            <EstimateSummaryPanel summary={summary} isLoading={!summary} />
          </div>
        </div>

        {/* Line Item Drawer */}
        <LineItemDrawer
          isOpen={lineItemDrawerOpen}
          onClose={() => {
            setLineItemDrawerOpen(false);
            setEditingLineItem(null);
          }}
          onSubmit={handleLineItemSubmit}
          lineItem={editingLineItem}
          isLoading={creatingLineItem || updatingLineItem}
        />

        {/* Estimate Drawer (for edit from detail view) */}
        <EstimateDrawer
          isOpen={estimateDrawerOpen}
          onClose={() => {
            setEstimateDrawerOpen(false);
            setEditingEstimate(null);
          }}
          onSubmit={handleEstimateSubmit}
          estimate={editingEstimate}
          projectId={projectId}
          isLoading={creatingEstimate || updatingEstimate}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          state={confirmModal}
          onClose={() => setConfirmModal(initialConfirmState)}
        />
      </ModuleLayout>
    );
  }

  // List View
  return (
    <ModuleLayout
      title="Estimates"
      description="Manage project cost estimates and proposals"
      icon={<FileSpreadsheet className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New Estimate',
        onClick: handleCreateEstimate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={[
        { title: 'Total Estimates', value: estimates.length.toString() },
        {
          title: 'Draft',
          value: estimates.filter(e => e.status === 'draft').length.toString(),
        },
        {
          title: 'Approved',
          value: estimates.filter(e => e.status === 'approved').length.toString(),
        },
      ]}
    >
      {/* Filters */}
      <div className="mb-6">
        <EstimateFilters filters={filters} onFilterChange={setFilters} />
      </div>

      {/* Estimate List */}
      <EstimateList
        estimates={estimates}
        onSelect={handleSelectEstimate}
        onEdit={handleEditEstimate}
        onSubmitForReview={handleSubmitForReview}
        onApprove={handleApprove}
        onReject={handleReject}
        onCreateVersion={handleCreateVersion}
        onDelete={handleDeleteEstimate}
        isLoading={estimatesLoading}
        emptyMessage="No estimates found. Create your first estimate to get started."
      />

      {/* Estimate Drawer */}
      <EstimateDrawer
        isOpen={estimateDrawerOpen}
        onClose={() => {
          setEstimateDrawerOpen(false);
          setEditingEstimate(null);
        }}
        onSubmit={handleEstimateSubmit}
        estimate={editingEstimate}
        projectId={projectId}
        isLoading={creatingEstimate || updatingEstimate}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        state={confirmModal}
        onClose={() => setConfirmModal(initialConfirmState)}
      />
    </ModuleLayout>
  );
};

export default Estimates;
